import React, {useCallback, useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {Button, CollapsibleCard, EMPHASIS, FlexColumn, FlexRow, Icon, Label, Separator, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {QuoteActivityItem, QuoteActivityList} from './QuoteActivityList';
import {QuoteDecisionPanel} from './QuoteDecisionPanel';
import {QuoteDecision} from './recordQuoteDecision';
import {canRecord} from './quoteActivityCopy';

/**
 * The footer line, by status. Says what happened once there is an outcome, explains itself while a
 * revision is in flight, and asks for a decision until either. A status with no entry falls back to
 * the prompt rather than rendering an undefined message id.
 */
const STATUS_FOOTER: {[status: string]: string} = {
	APPROVED: 'quote_activity.decided_approved',
	DECLINED: 'quote_activity.decided_declined',
	REVISING: 'quote_activity.revising_in_progress',
};

const fragment = graphql`
	fragment QuoteActivitySection_project on ProjectType {
		id
		companyProjectId
		client {
			id
		}
		quote {
			id
			status
			revisionNumber
		}
		quoteActivities(first: 100) {
			edges {
				node {
					id
					eventType
					revisionNumber
					createdAt
					clientContactName
					reason
					person {
						id
						fullName
					}
				}
			}
		}
	}
`;

/**
 * Ids for the action labels, so each Button can point `aria-labelledby` at its own text.
 *
 * Static rather than generated: the section renders once per Baseline page, and the footer action
 * row and the decision panel replace one another, so no two of these are ever in the document at
 * the same time.
 */
const ACTION_LABEL_IDS = {
	approve: 'quote-approve-action-label',
	decline: 'quote-decline-action-label',
	revise: 'quote-revise-action-label',
};

/**
 * Button label with a semantically coloured leading icon. Button's own `leftIcon` tints via an
 * `iconColor` enum that the package does not export, so the icon is passed as a child instead —
 * Icon accepts the colour by name — with FlexRow supplying the gap.
 *
 * The text is wrapped so it can carry an id. BaseButton derives the button's accessible name from
 * `children.toString()`, which is the literal string "[object Object]" for any JSX child, and it
 * applies that after spreading the caller's props — so passing our own `aria-label` would simply be
 * overwritten. `aria-labelledby` survives the spread and outranks `aria-label` in the
 * accessible-name computation, so pointing it at this element is what a screen reader reads.
 */
const ActionLabel = ({
	icon,
	color,
	label,
	labelId,
}: {
	icon: string;
	color: string;
	label: string;
	labelId: string;
}): JSX.Element => (
	<FlexRow gap="s" alignItems="center">
		<Icon icon={icon} size="s" color={color} />
		<span id={labelId}>{label}</span>
	</FlexRow>
);

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Relay fragment reference
	project: any;
	/** The viewer's company, for the decision analytics event. */
	companyId?: string | null;
	onDecided?: () => void;
};

export const QuoteActivitySection = ({project, companyId, onDecided}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const data = useFragment(fragment, project);
	// CollapsibleCard is a controlled Radix collapsible: it forwards `open` but no onOpenChange, so
	// the toggle has to be driven from the trigger's own handler. Starts expanded.
	const [open, setOpen] = useState(true);
	// Which decision form is open inline, if any. Null shows the action row instead.
	const [activeDecision, setActiveDecision] = useState<QuoteDecision | null>(null);

	const quote = data && data.quote;
	const activityConnection = data && data.quoteActivities;

	const activities: QuoteActivityItem[] = useMemo(() => {
		if (!activityConnection || !activityConnection.edges) return [];
		return activityConnection.edges
			.filter((edge: {node?: QuoteActivityItem}) => edge && edge.node)
			.map((edge: {node: QuoteActivityItem}) => edge.node);
	}, [activityConnection]);

	const clientId = data && data.client ? data.client.id : null;

	const canDecide = hasPermission(PERMISSION_TYPE.QUOTE_APPROVE_DECLINE_REVISE);
	const status = quote ? quote.status : null;
	// One table, shaped like the server's: the greyed-out button and the server's rejection are the
	// same rule, so a status a decision cannot be recorded from cannot render an enabled button for
	// it. Approve and Decline share a row, so one check answers for both.
	const canDecideOutcome = canRecord('APPROVED', status);
	const canRevise = canRecord('REVISING', status);
	const actionsEnabled = canDecide && canDecideOutcome;
	const reviseEnabled = canDecide && canRevise;
	// Both failure modes look identical to the user; only the tooltip differs, so it names the
	// actual reason rather than a generic "not available". `title` rather than a Tooltip wrapper
	// because it is the one hint that still shows on a disabled control.
	// An action the status disallows is dropped entirely, so the only reason a rendered one can be
	// present but unusable is the permission.
	const disabledReason = canDecide ? undefined : formatMessage({id: 'quote_activity.disabled_no_permission'});
	// Says what happened once there is an outcome, and asks for one until then.
	const footerMessageId = (status && STATUS_FOOTER[status]) || 'quote_activity.awaiting_decision';

	const afterDecision = useCallback(
		(action: 'Approved' | 'Declined' | 'Revised') => () => {
			trackEvent('Quote', action, {
				projectId: data ? data.companyProjectId : null,
				companyId,
				revisionNumber: quote ? quote.revisionNumber : null,
			});
			if (onDecided) onDecided();
		},
		[data, companyId, quote, onDecided]
	);

	const closePanel = useCallback(() => setActiveDecision(null), []);

	// The resolver returns null for quoteActivities unless quotes are available AND the viewer
	// holds QUOTE_VIEW_ACTIVITY, so visibility is enforced by the ABSENCE of data. The permission
	// check here is not the real gate — it only stops an empty section flashing before data arrives.
	if (!quote || !activityConnection) return null;
	if (!hasPermission(PERMISSION_TYPE.QUOTE_VIEW_ACTIVITY)) return null;

	return (
		<CollapsibleCard open={open}>
			<CollapsibleCard.Header>
				<FlexRow justifyContent="space-between" alignItems="center" data-cy="quote-activity-section">
					<FlexRow gap="s" alignItems="center">
						<Icon icon="clock" size="m" />
						<Label>{formatMessage({id: 'quote_activity.section_title'})}</Label>
					</FlexRow>
					<CollapsibleCard.Trigger>
						<Button
							iconOnly
							icon={open ? 'chevronUp' : 'chevronDown'}
							emphasis={EMPHASIS.LOW}
							onPress={() => setOpen(current => !current)}
							data-cy="quote-activity-toggle"
						/>
					</CollapsibleCard.Trigger>
				</FlexRow>
			</CollapsibleCard.Header>
			<CollapsibleCard.Content>
				<FlexColumn gap="m">
					<QuoteActivityList activities={activities} />
					<Separator />
					{activeDecision ? (
						<QuoteDecisionPanel
							quoteId={quote.id}
							clientId={clientId}
							decision={activeDecision}
							onCancel={closePanel}
							onDecided={afterDecision(
								activeDecision === 'APPROVED'
									? 'Approved'
									: activeDecision === 'DECLINED'
									? 'Declined'
									: 'Revised'
							)}
						/>
					) : (
						<FlexRow justifyContent="space-between" alignItems="center" gap="m">
							{/* Muted, at the same body size the log entries use, so the card reads as one
						    block of type rather than three different scales. */}
							<Text size="1" color="auxiliary">
								{formatMessage({id: footerMessageId})}
							</Text>
							<FlexRow gap="s" alignItems="center" justifyContent="flex-end">
								{/* Recording an outcome is only offered while there is none: a decided quote
							    leaves Revise as the sole way forward, and a revising one offers nothing. */}
								{canDecideOutcome && (
									<Button
										onPress={() => setActiveDecision('APPROVED')}
										disabled={!actionsEnabled}
										emphasis={EMPHASIS.MEDIUM}
										title={disabledReason}
										aria-labelledby={ACTION_LABEL_IDS.approve}
										data-cy="approve-quote-button"
									>
										<ActionLabel
											icon="checkCircle"
											color={actionsEnabled ? 'success' : 'disabled'}
											label={formatMessage({id: 'quote_activity.approve'})}
											labelId={ACTION_LABEL_IDS.approve}
										/>
									</Button>
								)}
								{canDecideOutcome && (
									<Button
										onPress={() => setActiveDecision('DECLINED')}
										disabled={!actionsEnabled}
										emphasis={EMPHASIS.MEDIUM}
										title={disabledReason}
										aria-labelledby={ACTION_LABEL_IDS.decline}
										data-cy="decline-quote-button"
									>
										<ActionLabel
											icon="closeCircle"
											color={actionsEnabled ? 'error' : 'disabled'}
											label={formatMessage({id: 'quote_activity.decline'})}
											labelId={ACTION_LABEL_IDS.decline}
										/>
									</Button>
								)}
								{canRevise && (
									<Button
										onPress={() => setActiveDecision('REVISING')}
										disabled={!reviseEnabled}
										emphasis={EMPHASIS.MEDIUM}
										title={disabledReason}
										aria-labelledby={ACTION_LABEL_IDS.revise}
										data-cy="revise-quote-button"
									>
										<ActionLabel
											icon="edit"
											color={reviseEnabled ? 'success' : 'disabled'}
											label={formatMessage({id: 'quote_activity.revise'})}
											labelId={ACTION_LABEL_IDS.revise}
										/>
									</Button>
								)}
							</FlexRow>
						</FlexRow>
					)}
				</FlexColumn>
			</CollapsibleCard.Content>
		</CollapsibleCard>
	);
};
